import { memo, useCallback } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { CircleAlertIcon, ExternalLinkIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { InferType } from 'yup';

import { ApiError } from '@/api/fetcher';
import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import Link from '@/components/atoms/Link/Link';
import { WarningAlert } from '@/components/atoms/WarningAlert/WarningAlert';
import {
  MODAL_DELETE,
  MODAL_EDIT,
  MODAL_IMPORT,
  MODAL_WARN,
} from '@/components/organisms/Modals/ModalConductor';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import useDialogNodesCache from '@/hooks/useDialogNodesCache';
import useDialogs from '@/hooks/useDialogs';
import useEntities from '@/hooks/useEntities';
import { AI_AGENT_REMINDER_FORM_ID } from '@/modules/aiAgents/constants';
import { useDialogReminder } from '@/modules/aiAgents/hooks/useDialogReminder';
import { hasDialogAnyErrors } from '@/redux/dialogAlerts/selectors';
import { popModal, pushModal } from '@/redux/modals/actions';
import { clearDialogNodes, updateDialogData } from '@/redux/nodes/actions';
import { selectNodeIds, selectIsNodesDirty, selectDraftDialogName } from '@/redux/nodes/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { OptionBase } from '@/shared/types/common';
import { RootState } from '@/shared/types/state';
import {
  isDialogReferencedInGuidelines,
  removeToolReferencesFromGuidelines,
} from '@/util/guidelineToolReferences';
import { getRestrictedNames } from '@/util/util';
import { MAX_DIALOG_NAME_LENGTH } from '@/util/validation/constants';
import { dialogRules } from '@/util/validation/rules/entity-rules';
import { getEditableTextValidationSchema } from '@/util/validation/schema-builders';

import SubHeader from './SubHeader';

type UsedNodes = OptionBase<{ dialogId: string }>[];

interface RenameProps {
  new_name: string;
}

const SubHeaderDialog = () => {
  const { t } = useTranslation();
  const { brainId, dialogId } = useParams();
  const isDraft = dialogId === 'draft';
  const {
    dialogs,
    dialog,
    deleteDialog,
    exportDialog,
    saveDialog,
    updateDialogName,
    importDialog,
    updateStatus,
    createStatus,
  } = useDialogs(brainId, dialogId);
  const { brain, patchBrainGuidelinesAsync } = useBrains(brainId);
  const isUnknown = dialog?.nodes[0]?.type === 'unknown';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const { entities } = useEntities(brainId);

  const { isDirty, draftDialogName, nodes, hasErrors } = useSelector(
    (state: RootState) => ({
      nodes: selectNodeIds(state),
      isDirty: selectIsNodesDirty(state),
      draftDialogName: selectDraftDialogName(state),
      hasErrors: hasDialogAnyErrors(state),
    }),
    shallowEqual
  );
  const { hasReminders } = useDialogReminder(dialogId);
  // Guards deletion of a dialog that holds the brain's starting node so a stored
  // starting_node_id can't dead-end deletion.
  const startingNodeInDialog = brain?.starting_node_id
    ? dialog?.nodes?.find((n) => n.node_id === brain?.starting_node_id)
    : undefined;
  const isStartingNodeInDialog = Boolean(startingNodeInDialog);

  const { findUsedNodes } = useDialogNodesCache();

  const handleDialogNameClick = useCallback(
    (dialog_id) => {
      dispatch(popModal());
      navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialog_id}`);
    },
    [brainId, dispatch, navigate, slug]
  );

  const handleSaveClick = useCallback(async () => {
    try {
      await saveDialog();
    } catch (error) {
      const { code } = error as ApiError;
      if (code === 'node_still_referenced') {
        toast.error(t('dialog.cannot_save_referenced'));
        return;
      }
      if (code === 'trigger_not_found') {
        toast.error(t('dialog.cannot_save_stale_trigger'));
        return;
      }
      console.error(error);
    }
  }, [saveDialog, t]);

  const onDeleteClick = useCallback(() => {
    const usedNodes = nodes.reduce((acc, nodeId) => {
      const cache = findUsedNodes({ brainId, name: nodeId }) || [];
      return [...acc, ...cache];
    }, [] as UsedNodes);
    if (
      usedNodes &&
      !isEmpty(usedNodes) &&
      !(usedNodes.length === 1 && dialogId === usedNodes[0].dialogId)
    ) {
      const warning = (
        <AlertWithReferences references={usedNodes} onRefClick={handleDialogNameClick}>
          <Trans i18nKey="dialog.delete_node" values={[dialog?.name]} />
        </AlertWithReferences>
      );

      const warnProps = {
        title: t('common.warning'),
        children: warning,
        primaryButtonText: t('common.close'),
      };

      dispatch(pushModal(MODAL_WARN, warnProps));
    } else if (hasReminders) {
      const warning = (
        <WarningAlert>
          <p>
            <Trans
              i18nKey="dialog.delete_node_with_reminders"
              values={[dialog?.name]}
              components={{
                link1: (
                  <Link
                    onClick={() => {
                      dispatch(popModal());
                      navigate(
                        `/${slug}/ai-agents/${brainId}/overview#${AI_AGENT_REMINDER_FORM_ID}`
                      );
                    }}
                  />
                ),
              }}
            />
          </p>
        </WarningAlert>
      );

      const warnProps = {
        title: t('common.warning'),
        children: warning,
        primaryButtonText: t('common.close'),
      };

      dispatch(pushModal(MODAL_WARN, warnProps));
    } else if (isStartingNodeInDialog) {
      const warning = (
        <WarningAlert>
          <p>
            <Trans
              i18nKey="dialog.delete_dialog_with_starting_node"
              values={[dialog?.name, startingNodeInDialog?.name]}
              components={{
                link1: (
                  <Link
                    onClick={() => {
                      dispatch(popModal());
                      navigate(`/${slug}/ai-agents/${brainId}/settings#conversation-start`);
                    }}
                  />
                ),
              }}
            />
          </p>
        </WarningAlert>
      );

      const warnProps = {
        title: t('common.warning'),
        children: warning,
        primaryButtonText: t('common.close'),
      };

      dispatch(pushModal(MODAL_WARN, warnProps));
    } else {
      const dialogName = dialog?.name ?? '';
      const nodeId = dialog?.nodes?.[0]?.node_id ?? '';
      const hasGuidelineRef = isDialogReferencedInGuidelines(brain?.guidelines, nodeId);

      const deleteProps = {
        title: t('dialog.delete_dialog'),
        subtitle: <Trans i18nKey="dialog.delete_dialog_warning" values={[dialogName]} />,
        warning: hasGuidelineRef ? (
          <div className="flex flex-col gap-3">
            <Alert variant="destructive" className="gap-y-2">
              <CircleAlertIcon />
              <AlertTitle className="text-sm leading-5 font-semibold tracking-normal">
                {t('local_tools.delete_dialog_guideline_warning_title')}
              </AlertTitle>
              <AlertDescription>
                {t('local_tools.delete_dialog_guideline_warning_description')}
              </AlertDescription>
            </Alert>
            <Button
              variant="link"
              className="h-auto w-fit p-0"
              onClick={() => {
                dispatch(popModal());
                navigate(`/${slug}/ai-agents/${brainId}/guidelines`);
              }}
            >
              {t('local_tools.view_guideline')}
              <ExternalLinkIcon />
            </Button>
          </div>
        ) : undefined,
        onDelete: async () => {
          dispatch(clearDialogNodes());
          deleteDialog(null, {
            onSuccess: async () => {
              if (hasGuidelineRef && brainId) {
                const patch = removeToolReferencesFromGuidelines(brain?.guidelines, nodeId);
                if (Object.keys(patch).length > 0) {
                  await patchBrainGuidelinesAsync({
                    brain_id: brainId,
                    preventToast: true,
                    ...patch,
                  });
                }
              }
            },
            onError: (error) => {
              if ((error as ApiError).code === 'node_still_referenced') {
                dispatch(popModal());
                toast.error(t('dialog.cannot_delete_referenced'));
              }
            },
          });
        },
        secondaryButtonText: t('common.cancel'),
      };

      dispatch(pushModal(MODAL_DELETE, deleteProps));
    }
  }, [
    brain?.guidelines,
    brainId,
    deleteDialog,
    dialog?.name,
    dialog?.nodes,
    dialogId,
    dispatch,
    findUsedNodes,
    handleDialogNameClick,
    hasReminders,
    isStartingNodeInDialog,
    startingNodeInDialog?.name,
    navigate,
    nodes,
    patchBrainGuidelinesAsync,
    slug,
    t,
  ]);

  const handleImportDialog = useCallback(
    (e) => {
      const referencedNodeIds: string[] = [];
      const usedNodes = nodes.reduce((acc, nodeId) => {
        const cache = findUsedNodes({ brainId, name: nodeId }) || [];
        if (cache.length > 0) {
          referencedNodeIds.push(nodeId);
        }
        return [...acc, ...cache];
      }, [] as UsedNodes);

      if (isEmpty(usedNodes)) {
        importDialog({ e, entities });
        e.target.value = null;
      } else {
        const warning = (
          <AlertWithReferences references={usedNodes} onRefClick={handleDialogNameClick}>
            {t('dialog.import_node')}
          </AlertWithReferences>
        );
        const importProps = {
          subtitle: <Trans i18nKey="dialog.import_dialog_warning" values={[dialog?.name]} />,
          warning,
          onImport: () => {
            importDialog({ e, entities, preserveNodeIds: referencedNodeIds });
            e.target.value = null;
          },
          onBlur: () => (e.target.value = null),
        };
        dispatch(pushModal(MODAL_IMPORT, importProps));
      }
    },
    [
      brainId,
      dialog?.name,
      dispatch,
      entities,
      findUsedNodes,
      handleDialogNameClick,
      importDialog,
      nodes,
      t,
    ]
  );

  const handleNameChange = useCallback(
    (text: string) => {
      dispatch(updateDialogData({ new_name: text }));
    },
    [dispatch]
  );

  // Updates the dialog name using the edit info modal
  const onEditClick = useCallback(() => {
    const renameProps = {
      title: t('common.dialog'),
      fieldName: 'new_name',
      fieldValue: draftDialogName ?? '',
      rules: dialogRules.name,
      resource: t('common.dialog'),
      onEdit: async (data: RenameProps) => {
        if (isDraft) {
          dispatch(popModal());
        } else {
          await updateDialogName({ dialog_id: dialog?.dialog_id, name: data.new_name });
        }
        dispatch(updateDialogData(data));
      },
    };

    dispatch(pushModal(MODAL_EDIT, renameProps));
  }, [dialog?.dialog_id, dispatch, draftDialogName, isDraft, updateDialogName, t]);

  // Filters out the current dialog name from the list of restricted values
  const restrictedValues = getRestrictedNames(dialogs, dialog?.name);

  const editableTextValidationSchema = getEditableTextValidationSchema(
    MAX_DIALOG_NAME_LENGTH,
    restrictedValues,
    t('intent.dialog_name')
  );

  type YupSchemaType = InferType<typeof editableTextValidationSchema>;

  return (
    <SubHeader<YupSchemaType>
      title={draftDialogName}
      onImportClick={handleImportDialog}
      onExportClick={exportDialog}
      onDeleteClick={onDeleteClick}
      onSaveClick={handleSaveClick}
      onNameChange={handleNameChange}
      disableRename={isUnknown}
      disableSave={!isDirty || hasErrors}
      disableDelete={isDraft || isUnknown}
      fileExtension=".json"
      isLoading={updateStatus === 'pending' || createStatus === 'pending'}
      onEditClick={onEditClick}
      editableTextValidationSchema={editableTextValidationSchema as YupSchemaType}
      type="dialog"
    />
  );
};

export default memo(SubHeaderDialog);
